import {useRef} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    DriveFileMove as MoveIcon,
    Edit as EditIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import {PendingImage} from '../../types/pending_docs';
import {encodeImageFilename} from '../../utils/url';

export interface ImageGalleryProps {
    /** Pending document ID for image URLs */
    pendingDocId: string;
    /** Images assigned to the current chapter */
    chapterImages: PendingImage[];
    /** Images not assigned to any chapter */
    unassignedImages: PendingImage[];
    /** Whether the gallery is expanded */
    expanded: boolean;
    /** Toggle expanded state */
    onToggleExpanded: () => void;
    /** Whether image operations are in progress */
    loading?: boolean;
    /** Called when upload button is clicked (file selected via input) */
    onUpload: (file: File) => void;
    /** Called when rename button is clicked */
    onRename: (image: PendingImage) => void;
    /** Called when move button is clicked */
    onMove: (image: PendingImage) => void;
    /** Called when delete button is clicked */
    onDelete: (image: PendingImage) => void;
}

/**
 * ImageGallery - Collapsible image gallery for pending documents.
 *
 * Displays chapter images and unassigned images with upload/rename/move/delete actions.
 */
export function ImageGallery({
    pendingDocId,
    chapterImages,
    unassignedImages,
    expanded,
    onToggleExpanded,
    loading = false,
    onUpload,
    onRename,
    onMove,
    onDelete,
}: ImageGalleryProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                // Let parent handle error display
                return;
            }
            onUpload(file);
        }
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const renderImageCard = (img: PendingImage, isUnassigned = false) => (
        <Box
            key={img.id}
            sx={{
                width: {xs: 120, sm: 150},
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: isUnassigned ? 'warning.light' : 'action.hover',
                '&:hover': {
                    bgcolor: isUnassigned ? 'warning.light' : 'action.selected',
                    boxShadow: 1,
                },
            }}
        >
            {/* Image thumbnail */}
            <Box
                role="button"
                tabIndex={0}
                aria-label={`View full image: ${img.filename}`}
                sx={{
                    height: {xs: 80, sm: 100},
                    overflow: 'hidden',
                    cursor: 'pointer',
                    bgcolor: 'action.selected',
                    '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: -2,
                    },
                }}
                onClick={() => {
                    window.open(
                        `/api/pending-docs/${pendingDocId}/images/${encodeImageFilename(img.filename)}`,
                        '_blank'
                    );
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        window.open(
                            `/api/pending-docs/${pendingDocId}/images/${encodeImageFilename(img.filename)}`,
                            '_blank'
                        );
                    }
                }}
            >
                <img
                    src={`/api/pending-docs/${pendingDocId}/images/${encodeImageFilename(img.filename)}`}
                    alt={img.filename}
                    loading="lazy"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                    }}
                />
            </Box>
            {/* Image info and actions */}
            <Box sx={{p: 1}}>
                <Tooltip title={img.filename}>
                    <Typography
                        variant="caption"
                        noWrap
                        sx={{display: 'block', mb: 0.5}}
                    >
                        {img.filename}
                    </Typography>
                </Tooltip>
                <Box sx={{display: 'flex', gap: 0.25, justifyContent: 'center'}}>
                    <Tooltip title="Rename">
                        <IconButton
                            onClick={() => onRename(img)}
                            disabled={loading}
                            aria-label={`Rename ${img.filename}`}
                            sx={{p: '6px'}}
                        >
                            <EditIcon sx={{fontSize: '1rem'}}/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Move to chapter">
                        <IconButton
                            onClick={() => onMove(img)}
                            disabled={loading}
                            color={isUnassigned ? 'warning' : 'default'}
                            aria-label={`Move ${img.filename} to chapter`}
                            sx={{p: '6px'}}
                        >
                            <MoveIcon sx={{fontSize: '1rem'}}/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            onClick={() => onDelete(img)}
                            disabled={loading}
                            color="error"
                            aria-label={`Delete ${img.filename}`}
                            sx={{p: '6px'}}
                        >
                            <DeleteIcon sx={{fontSize: '1rem'}}/>
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{p: 2}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <IconButton size="small" onClick={onToggleExpanded} aria-label={expanded ? 'Collapse images' : 'Expand images'}>
                        {expanded ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                    </IconButton>
                    <Typography
                        variant="subtitle2"
                        onClick={onToggleExpanded}
                        sx={{
                            color: "text.secondary",
                            cursor: 'pointer'
                        }}>
                        IMAGES ({chapterImages.length}
                        {unassignedImages.length > 0 ? ` + ${unassignedImages.length} unassigned` : ''})
                    </Typography>
                    <Tooltip title="Images are shared across all chapters. Moving an image changes which chapter's gallery displays it, but the image remains accessible from any chapter's markdown.">
                        <InfoIcon fontSize="small" color="action" sx={{opacity: 0.6}}/>
                    </Tooltip>
                </Box>
                <Box sx={{display: 'flex', gap: 1}}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={loading ? <CircularProgress size={16}/> : <UploadIcon/>}
                        onClick={handleUploadClick}
                        disabled={loading}
                    >
                        Upload
                    </Button>
                </Box>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    style={{display: 'none'}}
                    aria-label="Upload image file"
                />
            </Box>
            <Collapse in={expanded}>
                <Box sx={{mt: 1.5}}>
                    {chapterImages.length === 0 ? (
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                                py: 2,
                                textAlign: 'center'
                            }}>
                            No images in this chapter. Upload an image to add one.
                        </Typography>
                    ) : (
                        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                            {chapterImages.map(img => renderImageCard(img))}
                        </Box>
                    )}

                    {/* Unassigned Images Section */}
                    {unassignedImages.length > 0 && (
                        <>
                            <Divider sx={{my: 2}}/>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    display: 'block',
                                    mb: 1
                                }}>
                                UNASSIGNED ({unassignedImages.length}) - use move button to assign to a chapter
                            </Typography>
                            <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                                {unassignedImages.map(img => renderImageCard(img, true))}
                            </Box>
                        </>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}

export type {PendingImage};
